import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { cn } from '@sglara/cn';
import CheckCircleFilled from '../../assets/icons/checkCircleFilled.svg?react';
import Warning from '../../assets/icons/warning.svg?react';
import Info from '../../assets/icons/infoIcon.svg?react';
import ChevronRight from '../../assets/icons/chevronRight.svg?react';
import Close from '../../assets/icons/close.svg?react';
import { Typography } from '../Typography/Typography';
import { IconButton } from '../Button/Button';

type BannerVariant = 'success' | 'warning' | 'error' | 'neutral';

const variantClasses = {
  success: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-900',
  error: 'bg-red-100 text-red-800',
  neutral: 'bg-grayscale-50 text-grayscale-700',
};

const iconClasses = {
  success: 'text-green-500',
  warning: 'text-yellow-600',
  error: 'text-red-500',
  neutral: 'text-grayscale-600',
};

const variantHoverColors = {
  success: 'var(--green-200)',
  warning: 'var(--yellow-200)',
  error: 'var(--red-200)',
  neutral: 'var(--grayscale-100)',
};

const StyledCloseButton = styled(IconButton)<{ $variant: BannerVariant }>`
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 1;

  &:hover,
  &:focus-visible {
    background: ${({ $variant }) => variantHoverColors[$variant]} !important;
    border-color: transparent !important;
  }
`;

export type BannerProps = {
  variant: BannerVariant;
  title?: string;
  description?: string | React.ReactNode;
  url?: string;
  className?: string;
  /**
   * Callback function when the banner is dismissed. If provided, shows a close button.
   */
  onDismiss?: () => void;
};

/**
 * A banner that appears across the top of the page.
 */
export const Banner = ({ variant, title, description, url, className, onDismiss }: BannerProps) => {
  const Component = url ? Link : 'div';
  const componentProps = url ? { to: url } : {};

  let icon = <Warning height="20px" width="20px" aria-hidden />;
  if (variant === 'success') {
    icon = <CheckCircleFilled height="20px" width="20px" aria-hidden />;
  } else if (variant === 'neutral') {
    icon = <Info height="20px" width="20px" aria-hidden />;
  }

  const iconClass = iconClasses[variant];
  const allClasses = cn(
    // transparent border is for windows high contrast mode
    'border-transparent border border-solid p-4 w-full box-border flex items-start justify-center gap-2 no-underline',
    variantClasses[variant],
    // Add right padding when dismissible to make room for the close button
    onDismiss && 'pr-14',
    className,
  );

  const titleClasses = cn('flex items-center gap-2', { underline: url });

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDismiss?.();
  };

  return (
    <div className="w-full relative">
      <Component className={allClasses} {...(componentProps as any)}>
        <div className={iconClass}>{icon}</div>
        <div className="flex flex-col flex-wrap">
          {title && (
            <div className={titleClasses}>
              <Typography weight={600}>{title}</Typography>
              {url && <ChevronRight aria-hidden width="12px" height="12px" />}
            </div>
          )}
          {description && <Typography>{description}</Typography>}
        </div>
      </Component>

      {/* Add close button as a sibling of the banner because the banner may be a link, and it's an HTML violation to have a button inside a link */}
      {onDismiss && (
        <StyledCloseButton
          type="button"
          onClick={handleDismiss}
          $variant={variant}
          aria-label="Dismiss banner"
          size="sm"
        >
          <Close height="16px" width="16px" aria-hidden />
        </StyledCloseButton>
      )}
    </div>
  );
};

export default Banner;
